import { useAuthStore } from '@/stores/auth';
import { ensureValidToken, refreshAccessToken, login } from '@/auth/oidc';
import { tokenStorage, getStoredTokens } from '@/auth/token';
import type { ApiResponse } from '@/types/api';
import { ApiError } from '@/api/errors';
import { connectivity } from '@/api/connectivity';

export { ApiError };

export type ApiOptions = RequestInit & { retrySafe?: boolean };

const isRealAuth = import.meta.env.VITE_SKIP_AUTH !== 'true';

const DEPLOY_CLASS_STATUSES = new Set([502, 503, 504]);
const MAX_ATTEMPTS = 3; // 1 original + up to 2 replays. Applies across user-confirmed retries.

function withAuth(init?: HeadersInit): Headers {
  const token = useAuthStore.getState().token;
  const headers = new Headers(init);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (!isRealAuth) {
    // Dev mode: send full persona
    const user = useAuthStore.getState().user;
    if (user) {
      headers.set('X-Dev-Persona', JSON.stringify({
        name: user.name,
        email: user.email,
        roles: user.roles,
        odooId: user.odooId,
        // The Bright persona's own subject and login time (portal-api keys.ts owners).
        ...(user.authTime !== undefined ? { id: user.id, authTime: user.authTime } : {}),
      }));
    }
  } else {
    // Production: send role override if admin has one active
    const override = localStorage.getItem('dev-role-override');
    if (override) {
      headers.set('X-Dev-Persona', override);
    }
  }
  return headers;
}

async function refreshStoredToken(): Promise<void> {
  if (!isRealAuth) return;
  const valid = await ensureValidToken();
  if (valid) {
    const { accessToken, idToken } = getStoredTokens();
    if (accessToken && accessToken !== useAuthStore.getState().token && idToken) {
      useAuthStore.getState().updateToken(accessToken, idToken);
    }
  }
}

/**
 * The login headers `api()` sends, for a same-origin page that makes its own
 * requests — the framed opmeting tool (views/OpmetingView.tsx).
 */
export async function currentAuthHeaders(): Promise<Record<string, string>> {
  await refreshStoredToken();
  return Object.fromEntries(withAuth().entries());
}

async function doFetch(path: string, options?: RequestInit): Promise<Response> {
  const baseUrl = import.meta.env.VITE_API_URL || '';
  const headers = withAuth(options?.headers);

  if (options?.body && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  try {
    return await fetch(`${baseUrl}${path}`, { ...options, headers });
  } catch {
    throw new ApiError('NETWORK_ERROR', 'Kan geen verbinding maken met de server', 0);
  }
}

function parseErrorResponse(json: ApiResponse<unknown> | null, status: number, statusText: string): ApiError {
  if (json && !json.ok) {
    const extras = json as unknown as Record<string, unknown>;
    return new ApiError(json.code, json.message, status, extras);
  }
  return new ApiError(`HTTP_${status}`, `Server fout: ${status} ${statusText}`, status);
}

function isSafeMethod(options?: ApiOptions): boolean {
  const m = (options?.method || 'GET').toUpperCase();
  return m === 'GET' || m === 'HEAD';
}

/**
 * Handle a deploy-class failure (NETWORK_ERROR / 502 / 503 / 504).
 * Returns true if caller should replay; throws if the failure is terminal.
 */
async function handleDeployClassFailure(
  options: ApiOptions | undefined,
  attempts: number,
): Promise<boolean> {
  if (attempts >= MAX_ATTEMPTS) return false;

  // FormData multipart cannot be replayed (stream consumed). Throw immediately so
  // existing per-call retry loops (e.g. uploadCollectorPhoto) can rebuild the body
  // and retry themselves.
  if (options?.body instanceof FormData) return false;

  const retrySafe = options?.retrySafe === true;

  if (isSafeMethod(options) || retrySafe) {
    await connectivity.waitForReconnect(options?.signal ?? undefined);
    return true;
  }

  // Unsafe JSON write — recover, then ask the user.
  const decision = await connectivity.waitForUserDecision(options?.signal ?? undefined);
  if (decision === 'retry') return true;
  // 'cancel' — user chose to abandon the action.
  throw new ApiError('NETWORK_INTERRUPTED', 'Actie mogelijk niet voltooid', 0);
}

/**
 * Shared auth + retry + error-handling core.
 *
 * - Proactively refreshes the access token before sending.
 * - Replays on deploy-class failures per `handleDeployClassFailure`.
 * - On 401: refreshes, retries once; if still 401, clears auth + redirects to login.
 * - On any other non-OK status: parses the JSON error body and throws ApiError.
 *
 * Returns a successful (2xx) Response without consuming its body — callers are
 * responsible for `.json()`, `.blob()`, etc.
 */
async function _apiCore(path: string, options?: ApiOptions): Promise<Response> {
  await refreshStoredToken();

  let attempts = 0;
  while (true) {
    attempts++;

    let response: Response;
    try {
      response = await doFetch(path, options);
    } catch (err) {
      // doFetch wraps fetch rejections as ApiError('NETWORK_ERROR', ..., 0).
      if (err instanceof ApiError && err.code === 'NETWORK_ERROR') {
        const replay = await handleDeployClassFailure(options, attempts);
        if (replay) continue;
        throw err;
      }
      throw err;
    }

    // Deploy-class status — retry coordinator handles it.
    if (DEPLOY_CLASS_STATUSES.has(response.status)) {
      const replay = await handleDeployClassFailure(options, attempts);
      if (replay) continue;
      throw new ApiError(
        `HTTP_${response.status}`,
        `Server fout: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    // 401 refresh-then-retry path — preserved from prior behavior.
    if (response.status === 401 && isRealAuth) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        const { accessToken, idToken } = getStoredTokens();
        if (accessToken && idToken) {
          useAuthStore.getState().updateToken(accessToken, idToken);
        }
        response = await doFetch(path, options);
      }

      if (response.status === 401) {
        tokenStorage.clearTokens();
        useAuthStore.getState().clearAuth();
        login();
        throw new ApiError('UNAUTHORIZED', 'Sessie verlopen, opnieuw inloggen...', 401);
      }
    }

    // Other HTTP errors (4xx, 500, etc.) — parse JSON body if present, throw ApiError.
    if (!response.ok) {
      let json: ApiResponse<unknown> | null = null;
      try {
        json = await response.json();
      } catch {
        // Response wasn't JSON
      }
      throw parseErrorResponse(json, response.status, response.statusText);
    }

    connectivity.reportSuccess();
    return response;
  }
}

export async function api<T>(path: string, options?: ApiOptions): Promise<T> {
  const response = await _apiCore(path, options);
  let json: ApiResponse<T>;
  try {
    json = await response.json();
  } catch {
    throw new ApiError('PARSE_ERROR', 'Ongeldig antwoord van de server', response.status);
  }

  if (!json.ok) {
    throw new ApiError(json.code, json.message, response.status);
  }

  return json.data;
}

/**
 * Like `api<T>()` but returns the raw Response on 2xx so callers can call
 * `.blob()`, `.arrayBuffer()`, etc. Use for binary endpoints (preview/download).
 *
 * Non-2xx responses are still parsed and thrown as `ApiError`, so callers
 * never see error Responses.
 */
export async function apiFetch(path: string, options?: ApiOptions): Promise<Response> {
  return _apiCore(path, options);
}
